import { useState, useEffect, useCallback } from 'react';
import useDebounce from './useDebounce';
import useCVStoreRepository from '../store/cvStoreRepository';
import useDeliveryPositions from './useDeliveryPositions';
import { DEFAULT_COMPARISON_OPERATOR } from '../constants/comparisonOperators';

const useCVRepositorySearch = () => {
  const [searchInputValue, setSearchInputValue] = useState('');
  const [linkedFilter, setLinkedFilter] = useState(null); // null = all, true = linked, false = unlinked
  // Language filter state
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [levelComparator, setLevelComparator] = useState(DEFAULT_COMPARISON_OPERATOR);
  // Skills filter state
  const [selectedSkills, setSelectedSkills] = useState([]);
  
  const debouncedSearchTerm = useDebounce(searchInputValue, 500);
  
  // Get delivery positions hook
  const { selectedPositions, setSelectedPositions } = useDeliveryPositions();
  
  const {
    searchTerm,
    setSearchTerm,
    setScrollPosition,
    setLastCardId,
    setIsReturningFromDetails,
    setPositionIds,
    setLinkedFilter: setStoreLinkedFilter,
    setLanguageFilter: setStoreLanguageFilter,
    setLevelFilter: setStoreLevelFilter,
    setLevelComparator: setStoreLevelComparator,
    setSkillsFilter: setStoreSkillsFilter,
    uiSearchInput,
    setUISearchInput
  } = useCVStoreRepository();

  // Synchronize UI with store on mount and when uiSearchInput changes
  useEffect(() => {
    // Only sync from store to UI input when mounting or when store UI input explicitly changes
    // Remove the condition that forces empty local input back to previous searchTerm
    if (uiSearchInput !== undefined && uiSearchInput !== searchInputValue) {
      setSearchInputValue(uiSearchInput);
    }
  }, [uiSearchInput]);

  // Keep the store's uiSearchInput in sync with our local state
  useEffect(() => {
    setUISearchInput(searchInputValue);
  }, [searchInputValue, setUISearchInput]);
  
  // Create a wrapper for setting the search term that handles loading state
  const handleSearchTermChange = useCallback((term) => {
    // When term is empty and searchTerm is also empty, don't do anything
    if (term === '' && searchTerm === '') return;
    
    // Reset state is handled by the store's setSearchTerm implementation
    setSearchTerm(term);
  }, [searchTerm, setSearchTerm]);

  // Handle position change
  useEffect(() => {
    // Reset scroll position when positions change
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);
    
    // Update position IDs in store
    const positionIds = selectedPositions.length > 0 
      ? selectedPositions.map(pos => pos.id) 
      : null;
    
    setPositionIds(positionIds);
  }, [selectedPositions, setPositionIds, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Handle linked filter change
  useEffect(() => {
    // Reset scroll position when linked filter changes
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);
    
    // Update linked filter in store
    setStoreLinkedFilter(linkedFilter);
  }, [linkedFilter, setStoreLinkedFilter, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Handle language filter change
  useEffect(() => {
    // Reset scroll position when language filter changes
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);
    
    // Update language filter in store
    setStoreLanguageFilter(selectedLanguage?.id || null);
  }, [selectedLanguage, setStoreLanguageFilter, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Handle level filter change
  useEffect(() => {
    // Reset scroll position when level filter changes
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);
    
    // Update level filter in store
    setStoreLevelFilter(selectedLevel?.id || null);
  }, [selectedLevel, setStoreLevelFilter, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Handle level comparator change
  useEffect(() => {
    // Reset scroll position when level comparator changes
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);
    
    // Update level comparator in store
    setStoreLevelComparator(levelComparator);
  }, [levelComparator, setStoreLevelComparator, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Handle skills filter change
  useEffect(() => {
    // Reset scroll position when skills filter changes
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);
    
    // Update skills filter in store - pass array of skill IDs
    const skillIds = selectedSkills.length > 0 ? selectedSkills.map(skill => skill.id) : null;
    setStoreSkillsFilter(skillIds);
  }, [selectedSkills, setStoreSkillsFilter, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Handle search input with debounce
  useEffect(() => {
    // Skip the initial render when both terms are empty
    if (debouncedSearchTerm === '' && searchTerm === '' && searchInputValue === '') {
      return;
    }
    
    // Only proceed if the debounced term has actually changed
    if (debouncedSearchTerm === searchTerm) {
      return;
    }
    
    // Reset scroll position when actively searching
    setScrollPosition(0);
    setLastCardId(null);
    setIsReturningFromDetails(false);
    
    // Short delay to ensure UI updates before fetch operation
    const timer = setTimeout(() => {
      handleSearchTermChange(debouncedSearchTerm);
    }, 50);
    
    return () => clearTimeout(timer);
  }, [debouncedSearchTerm, searchTerm, searchInputValue, handleSearchTermChange, setScrollPosition, setLastCardId, setIsReturningFromDetails]);

  // Method to clear search filters
  const clearSearchFilters = useCallback(() => {
    setSearchInputValue('');
    setSearchTerm('');
    setSelectedPositions([]);
    setLinkedFilter(null);
    setSelectedLanguage(null);
    setSelectedLevel(null);
    setLevelComparator(DEFAULT_COMPARISON_OPERATOR);
    setSelectedSkills([]);
  }, [setSearchTerm, setSelectedPositions]);

  return { 
    searchInputValue, 
    setSearchInputValue,
    selectedPositions,
    setSelectedPositions,
    linkedFilter,
    setLinkedFilter,
    selectedLanguage,
    setSelectedLanguage,
    selectedLevel,
    setSelectedLevel,
    levelComparator,
    setLevelComparator,
    selectedSkills,
    setSelectedSkills,
    clearSearchFilters
  };
};

export default useCVRepositorySearch; 